import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { Transaction } from "../shared/types";
import type { AccountRow } from "../lib/accountRows";
import { fetchTransactions } from "../lib/transactions";
import { getStaleCache, setCache, txCacheKey, evictTxCaches } from "../lib/dataCache";
import { TxRow } from "./TxRow";
import { Spinner } from "./ui";
import { fetchAllTokenTransfers } from "../lib/chains/evmAdapter";
import { proxyEvmTokenTransfers } from "../lib/chains/chain-proxy-evm";
import { mergeEvmActivity } from "../lib/tx-classify";

interface ChainTxResult {
  chainName: string;
  chainIcon: string | null;
  explorerUrl: string;
  txs: Transaction[];
  hasMore: boolean;
  page: number;
  error: boolean;
}

/** Deduplicate accountRows by address+chainId (same address appears once per chain) */
function uniqueAddressChains(rows: AccountRow[]) {
  const seen = new Set<string>();
  return rows.filter((r) => {
    const key = `${r.address}:${r.chain.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Blockscout token transfers; when the explorer cannot be read, the chain proxy's erc20 rows of the listed tokens. */
async function loadEvmTokenTransfers(row: AccountRow): Promise<Transaction[]> {
  const viaExplorer = await fetchAllTokenTransfers(row.address, row.chain.explorerUrl);
  if (viaExplorer) return viaExplorer;
  return (await proxyEvmTokenTransfers(row.chain, row.address, row.assets).catch(() => null)) ?? [];
}

interface WalletActivityProps {
  accountRows: AccountRow[];
  pollInterval: number;
}

export function WalletActivity({ accountRows, pollInterval }: WalletActivityProps) {
  const { t } = useTranslation();
  const [results, setResults] = useState<Map<string, ChainTxResult>>(new Map());
  const [loading, setLoading] = useState(true);

  const uniqueRows = uniqueAddressChains(accountRows);

  // Load stale cache immediately on mount
  useEffect(() => {
    const initial = new Map<string, ChainTxResult>();
    let hasStale = false;
    for (const row of uniqueAddressChains(accountRows)) {
      const key = `${row.address}:${row.chain.id}`;
      const cacheK = txCacheKey(row.address, row.chain.id);
      const cached = getStaleCache<Transaction[]>(cacheK);
      if (cached) {
        hasStale = true;
        initial.set(key, {
          chainName: row.chain.displayName,
          chainIcon: row.chain.iconUrl,
          explorerUrl: row.chain.explorerUrl,
          txs: cached.data,
          hasMore: false,
          page: 1,
          error: false,
        });
      }
    }
    if (hasStale) {
      setResults(initial);
      setLoading(false); // Show stale data immediately, fetch fresh in background
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = useCallback(async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);

    const fetches = uniqueRows.map(async (row) => {
      const key = `${row.address}:${row.chain.id}`;
      const nativeAsset = row.assets.find((a) => a.isNative) ?? row.assets[0];
      if (!nativeAsset) return;
      try {
        // Fetch native transactions + token transfers in parallel
        const tokenAssets = row.assets.filter((a) => !a.isNative && a.contractAddress);
        const [nativeResult, tokenTxs] = await Promise.all([
          fetchTransactions(row.address, row.chain, nativeAsset, 1),
          row.chain.type === "evm"
            ? loadEvmTokenTransfers(row)
            : Promise.resolve([] as Transaction[]),
        ]);

        // Fetch token transactions for non-EVM chains (TON Jettons, etc.)
        let extraTokenTxs: Transaction[] = [];
        if (row.chain.type !== "evm" && tokenAssets.length > 0) {
          const results = await Promise.all(
            tokenAssets.map((asset) =>
              fetchTransactions(row.address, row.chain, asset, 1)
                .then((r) => r.transactions)
                .catch(() => [] as Transaction[])
            )
          );
          extraTokenTxs = results.flat();
        }

        // Merge and deduplicate by hash — token transfers first so they
        // take precedence over native txs that show 0 ETH for the same hash
        // EVM: one row per hash, with approvals / swaps / contract calls classified and unlisted-token spam dropped.
        const seen = new Set<string>();
        const merged = row.chain.type === "evm"
          ? mergeEvmActivity(nativeResult.transactions, tokenTxs, { address: row.address, assets: row.assets })
          : [...tokenTxs, ...extraTokenTxs, ...nativeResult.transactions]
              .filter((tx) => { if (seen.has(tx.hash)) return false; seen.add(tx.hash); return true; })
              .sort((a, b) => b.timestamp - a.timestamp);

        // Cache the merged transactions
        setCache(txCacheKey(row.address, row.chain.id), merged);
        setResults((prev) => {
          const next = new Map(prev);
          next.set(key, {
            chainName: row.chain.displayName,
            chainIcon: row.chain.iconUrl,
            explorerUrl: row.chain.explorerUrl,
            txs: merged,
            hasMore: nativeResult.hasMore,
            page: 1,
            error: false,
          });
          return next;
        });
      } catch {
        setResults((prev) => {
          const existing = prev.get(key);
          if (existing && isRefresh) return prev; // Keep stale data on refresh error
          const next = new Map(prev);
          next.set(key, {
            chainName: row.chain.displayName,
            chainIcon: row.chain.iconUrl,
            explorerUrl: row.chain.explorerUrl,
            txs: [],
            hasMore: false,
            page: 1,
            error: true,
          });
          return next;
        });
      }
    });

    await Promise.allSettled(fetches);
    evictTxCaches(50);
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountRows]);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(() => fetchAll(true), pollInterval);
    return () => clearInterval(iv);
  }, [fetchAll, pollInterval]);

  // Merge all transactions sorted by timestamp desc, with chain context
  const merged = Array.from(results.entries()).flatMap(([, result]) =>
    result.txs.map((tx) => ({
      tx,
      chainName: result.chainName,
      chainIcon: result.chainIcon,
      explorerUrl: result.explorerUrl,
    }))
  ).sort((a, b) => b.tx.timestamp - a.tx.timestamp);

  const failedChains = Array.from(results.values()).filter((r) => r.error);
  const allEmpty = !loading && merged.length === 0 && failedChains.length === 0;

  return (
    <div>
      {/* Loading skeleton */}
      {loading && merged.length === 0 && (
        <div className="bg-surface-secondary rounded-lg border border-border-primary overflow-hidden divide-y divide-border-secondary">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center px-4 py-3.5 animate-pulse">
              <div className="w-8 h-8 rounded-full bg-surface-tertiary shrink-0 mr-3" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3.5 w-24 bg-surface-tertiary rounded" />
                <div className="h-2.5 w-36 bg-surface-tertiary/60 rounded" />
              </div>
              <div className="space-y-1.5 text-right">
                <div className="h-3.5 w-16 bg-surface-tertiary rounded ml-auto" />
                <div className="h-2.5 w-10 bg-surface-tertiary/60 rounded ml-auto" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {allEmpty && (
        <div className="px-4 py-10 text-center">
          <svg className="w-8 h-8 text-text-muted mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
          </svg>
          <p className="text-sm text-text-tertiary">{t("walletActivity.noTransactionsYet")}</p>
          <p className="text-xs text-text-muted mt-1">{t("walletActivity.activityAcrossChains")}</p>
        </div>
      )}

      {/* Failed chains retry */}
      {failedChains.length > 0 && merged.length === 0 && !loading && (
        <div className="px-4 py-6 text-center">
          <p className="text-xs text-text-tertiary mb-1">
            {t("walletActivity.failedToLoadFrom", { chain: failedChains.map((c) => c.chainName).join(", ") })}
          </p>
          <button
            onClick={() => fetchAll()}
            className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
          >
            {t("walletActivity.tryAgain")}
          </button>
        </div>
      )}

      {/* Transaction list */}
      {merged.length > 0 && (
        <div className="bg-surface-secondary rounded-lg border border-border-primary overflow-hidden divide-y divide-border-secondary">
          {merged.map(({ tx, chainName, chainIcon, explorerUrl }, i) => (
            <div key={`${tx.hash}-${i}`}>
              <TxRow tx={tx} explorerUrl={explorerUrl} chainName={chainName} chainIcon={chainIcon} />
            </div>
          ))}
        </div>
      )}

      {/* Partial errors banner */}
      {failedChains.length > 0 && merged.length > 0 && (
        <div className="mt-2 flex items-center gap-2 px-1">
          <span className="text-[10px] text-text-muted">
            {t("walletActivity.couldNotLoad")}: {failedChains.map((c) => c.chainName).join(", ")}
          </span>
          <button
            onClick={() => fetchAll()}
            className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
          >
            {t("walletActivity.retry")}
          </button>
        </div>
      )}

      {/* Loading indicator during refresh */}
      {loading && merged.length > 0 && (
        <div className="flex items-center justify-center gap-2 mt-2">
          <Spinner size="xs" />
          <span className="text-[10px] text-text-muted">{t("walletActivity.updating")}</span>
        </div>
      )}

    </div>
  );
}
